import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';

import { Button } from '@/components/ui/catalyst/button';
import { Skeleton } from '@/components/ui/skeleton';
import { enhancedApi } from '@/services/api/policeDepartmentsService';

import { DepartmentSidebar } from './sections';
import { ActiveMattersSection } from './sections/ActiveMattersSection';
import { RequestMethodsSection } from './sections/RequestMethodsSection';

export const PoliceDepartmentDetailPage = () => {
  const { departmentId } = useParams<{ departmentId: string }>();

  const {
    data: department,
    isLoading,
    error: loadError,
  } = enhancedApi.usePoliceDepartmentsRetrieveQuery(
    { id: departmentId || '' },
    { skip: !departmentId },
  );

  if (!departmentId) {
    return (
      <div className="mx-auto max-w-7xl p-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">Department ID is required</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="border-b bg-white">
          <div className="mx-auto max-w-[1600px] px-6 py-4">
            <Button plain href="/policedepartments">
              <ArrowLeftIcon />
              Back to Departments
            </Button>
          </div>
        </div>
        <div className="mx-auto max-w-[1600px] px-6 py-6">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-800">
              Failed to load department details
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="border-b bg-white">
          <div className="mx-auto max-w-[1600px] px-6 py-4">
            <Button plain href="/policedepartments">
              <ArrowLeftIcon />
              Back to Departments
            </Button>
          </div>
        </div>
        <div className="mx-auto max-w-[1600px] px-6 py-6">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-3">
              <Skeleton className="h-[400px] w-full" />
            </div>
            <div className="col-span-9 space-y-6">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-96 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!department) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="border-b bg-white">
          <div className="mx-auto max-w-[1600px] px-6 py-4">
            <Button plain href="/policedepartments">
              <ArrowLeftIcon />
              Back to Departments
            </Button>
          </div>
        </div>
        <div className="mx-auto max-w-[1600px] px-6 py-6">
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <h3 className="text-sm font-medium text-gray-900">
              Department not found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              The requested police department could not be found.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Header */}
      <div className="border-b bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-4">
          <Button plain href="/policedepartments">
            <ArrowLeftIcon />
            Back to Departments
          </Button>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="mx-auto max-w-[1600px] px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Compact info */}
          <div className="col-span-3">
            <DepartmentSidebar department={department} />
          </div>

          {/* Main Content Column */}
          <div className="col-span-9 space-y-6">
            <RequestMethodsSection
              departmentId={departmentId}
              channels={department.records_channels || []}
            />
            <ActiveMattersSection departmentId={departmentId} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoliceDepartmentDetailPage;
